import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import type { GETpropertiesByIDsResponse } from '../infrastructure/HTTPPropertyRepository/schemas/properties/ids'
import { Property } from './Property'
import type { POSTRelatedResponseType } from '../infrastructure/HTTPPropertyRepository/schemas/related'
import type { Affiliate } from '../../shared/domain/Affiliate'
import type { Market } from '../../shared/domain/Market'
import type { POSTBannerResponseType } from '../infrastructure/HTTPPropertyRepository/schemas/banner'

export const ListOfPropertyValidation = z.object({
  list: z.instanceof(Property, { message: '[ListOfProperty] list is required' }).array(),
})

export class ListOfProperty extends Model {
  static create({ list }: z.infer<typeof ListOfPropertyValidation>) {
    ListOfPropertyValidation.parse({ list })
    return new ListOfProperty(list)
  }

  static fromAPI({ json }: { json: GETpropertiesByIDsResponse }) {
    return ListOfProperty.create({
      list: json.properties
        .filter(propertyFilter => propertyFilter.accommodation_type !== 'PENDING')
        .map(propertyJSON => Property.fromAPI({ json: propertyJSON })),
    })
  }

  static fromJSON({ json }: { json: ReturnType<ListOfProperty['toJSON']> }) {
    return ListOfProperty.create({
      list: json.list.map(propertyJSON => Property.fromJSON({ json: propertyJSON })),
    })
  }

  static fromJSONOrEmpty({ json }: { json: ReturnType<ListOfProperty['toJSON']> }) {
    try {
      if (!json) return ListOfProperty.empty()
      return this.fromJSON({ json })
    } catch {
      return ListOfProperty.empty()
    }
  }

  static fromRelatedAPI({
    json,
    affiliate,
    market,
  }: {
    json: POSTRelatedResponseType
    affiliate: Affiliate
    market: Market
  }) {
    return ListOfProperty.create({
      list: json.properties.map(propertyJSON =>
        Property.fromRelatedAPI({ json: propertyJSON, affiliate, market })
      ),
    })
  }

  static fromBannerApi({
    json,
    affiliate,
    market,
  }: {
    json: POSTBannerResponseType
    affiliate: Affiliate
    market: Market
  }) {
    return ListOfProperty.create({
      list: json.properties.map(propertyJSON =>
        Property.fromBannerApi({ json: propertyJSON, affiliate, market })
      ),
    })
  }

  static empty() {
    return new ListOfProperty([])
  }

  constructor(private readonly _list: Property[]) {
    super()
  }

  get list() { return this._list } // prettier-ignore

  get length() {
    return this._list.length
  }

  toJSON() {
    return { list: this._list.map(item => item.toJSON()) }
  }

  isEmpty() {
    return this._list.length === 0
  }

  at(index: number) {
    return this._list.at(index)
  }

  map<T>(callback: (property: Property, index: number) => T) {
    return this._list.map(callback)
  }

  removeById(id: ReturnType<Property['toJSON']>['id']) {
    const filteredProperties = this._list.filter(property => property.id !== id)
    return ListOfProperty.create({ list: filteredProperties })
  }

  getAllIds(): string[] {
    return this.list.map(property => property.id.toString())
  }
}
